import React, { useState, useMemo, useEffect } from 'react';
import {
    Calendar,
    ChevronLeft,
    ChevronRight,
    Settings,
    Filter,
    Plus,
    X,
    AlertCircle,
    Users,
    Clock,
    CheckCircle2,
    Search,
    Check,
    Moon,
    Sun,
    Menu
} from 'lucide-react';
import Layout from '../../Layout'

// Giả lập dữ liệu phòng mổ
const rooms = [
    { id: 'R1', name: 'Gold Meeting Room' },
    { id: 'R2', name: 'NTL Diamond' },
    { id: 'R3', name: 'Phòng Advance' },
    { id: 'R4', name: 'Phòng họp Diamond' },
    { id: 'R5', name: 'Ruby Meeting Room' },
    { id: 'R6', name: 'Silver Meeting Room' }
];

// Giả lập dữ liệu danh sách nhân viên
const staffList = [
    { id: 'S1', name: 'BS. Lê Thị Hoàng Yến', role: 'Bác sĩ phẫu thuật', avatar: 'https://i.pravatar.cc/150?u=a042581f4e29026024d' },
    { id: 'S2', name: 'BS. Nguyễn Văn An', role: 'Bác sĩ phẫu thuật', avatar: 'https://i.pravatar.cc/150?u=a042581f4e29026704d' },
    { id: 'S3', name: 'BS. Trần Kim Thu', role: 'Bác sĩ gây mê', avatar: 'https://i.pravatar.cc/150?u=a04258114e29026702d' },
    { id: 'S4', name: 'YT. Phạm Băng Băng', role: 'Y tá', avatar: 'https://i.pravatar.cc/150?u=a048581f4e29026701d' },
    { id: 'S5', name: 'YT. Hồ Ngọc Hà', role: 'Y tá', avatar: 'https://i.pravatar.cc/150?u=a092581d4ef9026700d' },
    { id: 'S6', name: 'BS. Đàm Vĩnh Hưng', role: 'Bác sĩ gây mê', avatar: 'https://i.pravatar.cc/150?u=a042581f4e29026024c' },
];

// Giả lập dữ liệu lịch mổ trong tuần (từ Thứ 2 đến Chủ nhật)
const mockSurgeries = [
    {
        id: 'Surg1',
        roomId: 'R4',
        dayIndex: 0, // Thứ 2
        title: 'Nâng mũi S-line',
        patient: 'Nguyễn Thị Hoa',
        time: '08:00 - 10:30',
        status: 'Đã duyệt',
        team: ['S1', 'S3', 'S4'], // Đủ team
        warning: false
    },
    {
        id: 'Surg2',
        roomId: 'R4',
        dayIndex: 1, // Thứ 3
        title: 'Cắt mí Hàn Quốc',
        patient: 'Trần Văn Beo',
        time: '11:27 - 12:27',
        status: 'Đã duyệt',
        team: ['S2'], // Thiếu người (gây mê, y tá)
        warning: true,
        warningMsg: 'Thiếu Bác sĩ gây mê, Y tá'
    },
    {
        id: 'Surg3',
        roomId: 'R4',
        dayIndex: 2, // Thứ 4
        title: 'Hút mỡ bụng',
        patient: 'Lê Vĩnh Thắng',
        time: '14:00 - 16:00',
        status: 'Đã duyệt',
        team: ['S1', 'S4'], // Thiếu gây mê
        warning: true,
        warningMsg: 'Thiếu Bác sĩ gây mê'
    },
    {
        id: 'Surg4',
        roomId: 'R4',
        dayIndex: 3, // Thứ 5
        title: 'Gọt hàm',
        patient: 'Bùi Thị Nụ',
        time: '09:00 - 12:00',
        status: 'Đã duyệt',
        team: ['S2', 'S3', 'S5'], // Đủ team
        warning: false
    }
];

export default function App() {
    const [surgeries, setSurgeries] = useState(mockSurgeries);

    // States for Create Surgery Modal
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [createData, setCreateData] = useState({
        roomId: '',
        dayIndex: 0,
        patient: '',
        service: '',
        time: '',
        selectedStaff: []
    });

    // States for Search and Filter in Create Modal
    const [searchQuery, setSearchQuery] = useState('');
    const [roleFilter, setRoleFilter] = useState('All');

    // States for Detail Modal
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [selectedSurgery, setSelectedSurgery] = useState(null);

    // States for Date Picker
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [currentYear, setCurrentYear] = useState(2026);
    const [currentMonth, setCurrentMonth] = useState(8); // August (1-indexed for display)

    const daysOfWeek = [
        { name: 'THỨ 2', date: '10' },
        { name: 'THỨ 3', date: '11' },
        { name: 'THỨ 4', date: '12' },
        { name: 'THỨ 5', date: '13' },
        { name: 'THỨ 6', date: '14', isToday: true },
        { name: 'THỨ 7', date: '15' },
        { name: 'CHỦ NHẬT', date: '16' },
    ];

    const handleCellClick = (roomId, dayIndex) => {
        setCreateData({
            ...createData,
            roomId: roomId,
            dayIndex: dayIndex
        });
        setIsCreateModalOpen(true);
    };

    const handleSurgeryClick = (surgery, e) => {
        e.stopPropagation(); // Ngăn sự kiện click lan ra ô
        setSelectedSurgery(surgery);
        setIsDetailModalOpen(true);
    };

    const checkTeamCompleteness = (teamIds) => {
        let hasSurgeon = false;
        let hasAnesthetist = false;
        let hasNurse = false;

        teamIds.forEach(id => {
            const staff = staffList.find(s => s.id === id);
            if (staff) {
                if (staff.role === 'Bác sĩ phẫu thuật') hasSurgeon = true;
                if (staff.role === 'Bác sĩ gây mê') hasAnesthetist = true;
                if (staff.role === 'Y tá') hasNurse = true;
            }
        });

        const missing = [];
        if (!hasSurgeon) missing.push('Bác sĩ phẫu thuật');
        if (!hasAnesthetist) missing.push('Bác sĩ gây mê');
        if (!hasNurse) missing.push('Y tá');

        return {
            isComplete: missing.length === 0,
            missingMsg: missing.length > 0 ? `Thiếu ${missing.join(', ')}` : ''
        };
    };

    const handleAddStaffToSurgery = (staffId) => {
        if (!selectedSurgery) return;

        const currentTeam = selectedSurgery.team;
        if (currentTeam.includes(staffId)) return; // Đã có trong team

        const newTeam = [...currentTeam, staffId];
        const { isComplete, missingMsg } = checkTeamCompleteness(newTeam);

        const updatedSurgery = {
            ...selectedSurgery,
            team: newTeam,
            warning: !isComplete,
            warningMsg: missingMsg
        };

        // Update in local state
        setSelectedSurgery(updatedSurgery);
        // Update in main list
        setSurgeries(surgeries.map(s => s.id === updatedSurgery.id ? updatedSurgery : s));
    };


    const renderCreateModal = () => {
        if (!isCreateModalOpen) return null;

        const toggleStaff = (id) => {
            const isSelected = createData.selectedStaff.includes(id);
            if (isSelected) {
                setCreateData({ ...createData, selectedStaff: createData.selectedStaff.filter(sId => sId !== id) });
            } else {
                setCreateData({ ...createData, selectedStaff: [...createData.selectedStaff, id] });
            }
        };

        const teamStatus = checkTeamCompleteness(createData.selectedStaff);

        const filteredStaff = staffList.filter(staff => {
            const matchesSearch = staff.name.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesRole = roleFilter === 'All' || staff.role === roleFilter;
            return matchesSearch && matchesRole;
        });

        return (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-[600px] max-h-[90vh] flex flex-col transition-colors">
                    <div className="flex justify-between items-center p-4 lg:p-5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-t-xl">
                        <h2 className="text-lg lg:text-xl font-bold text-gray-800 dark:text-gray-100">Tạo lịch phẫu thuật mới</h2>
                        <button onClick={() => setIsCreateModalOpen(false)} className="text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors">
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    <div className="p-4 lg:p-6 overflow-y-auto flex-1 space-y-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Khách hàng</label>
                                <input type="text" placeholder="Tên khách hàng" className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 outline-none transition-colors" />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Dịch vụ (Title)</label>
                                <input type="text" placeholder="VD: Cắt mí, Nâng mũi..." className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 outline-none transition-colors" />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Phòng mổ</label>
                                <select
                                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                                    value={createData.roomId}
                                    onChange={(e) => setCreateData({ ...createData, roomId: e.target.value })}
                                >
                                    <option value="">-- Chọn phòng --</option>
                                    {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                                </select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Ngày (Thứ trong tuần)</label>
                                <select
                                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                                    value={createData.dayIndex}
                                    onChange={(e) => setCreateData({ ...createData, dayIndex: parseInt(e.target.value) })}
                                >
                                    {daysOfWeek.map((d, idx) => <option key={idx} value={idx}>{d.name} ({d.date})</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
                                <h3 className="text-md font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                                    <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                    Đề xuất Ê-kíp thực hiện
                                </h3>
                                {!teamStatus.isComplete && (
                                    <span className="text-xs text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30 px-2 py-1 rounded-full font-medium flex items-center gap-1">
                                        <AlertCircle className="w-3 h-3" /> {teamStatus.missingMsg}
                                    </span>
                                )}
                                {teamStatus.isComplete && (
                                    <span className="text-xs text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded-full font-medium flex items-center gap-1">
                                        <CheckCircle2 className="w-3 h-3" /> Đủ ê-kíp
                                    </span>
                                )}
                            </div>

                            {/* Search and Filter Section */}
                            <div className="flex flex-col sm:flex-row gap-2 mb-4">
                                <div className="relative flex-1">
                                    <Search className="w-4 h-4 absolute left-3 top-[10px] transform text-gray-400 dark:text-gray-500" />
                                    <input
                                        type="text"
                                        placeholder="Tìm nhân viên..."
                                        className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-colors"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </div>
                                <select
                                    className="border border-gray-300 dark:border-gray-600 rounded-lg text-sm px-3 py-2 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-w-[150px] transition-colors"
                                    value={roleFilter}
                                    onChange={(e) => setRoleFilter(e.target.value)}
                                >
                                    <option value="All">Tất cả phòng ban</option>
                                    <option value="Bác sĩ phẫu thuật">Bác sĩ phẫu thuật</option>
                                    <option value="Bác sĩ gây mê">Bác sĩ gây mê</option>
                                    <option value="Y tá">Y tá</option>
                                </select>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[200px] overflow-y-auto p-1 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
                                {filteredStaff.length > 0 ? filteredStaff.map(staff => (
                                    <label key={staff.id} className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 text-blue-600 dark:text-blue-500 rounded border-gray-300 dark:border-gray-600 focus:ring-blue-500 dark:focus:ring-blue-400 dark:bg-gray-800"
                                            checked={createData.selectedStaff.includes(staff.id)}
                                            onChange={() => toggleStaff(staff.id)}
                                        />
                                        <img src={staff.avatar} alt={staff.name} className="w-8 h-8 rounded-full border border-gray-200 dark:border-gray-600" />
                                        <div>
                                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-tight">{staff.name}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">{staff.role}</p>
                                        </div>
                                    </label>
                                )) : (
                                    <div className="col-span-1 sm:col-span-2 text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
                                        Không tìm thấy nhân viên phù hợp.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="p-4 lg:p-5 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl flex justify-end gap-3">
                        <button onClick={() => setIsCreateModalOpen(false)} className="px-4 py-2 text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 font-medium transition-colors">
                            Hủy
                        </button>
                        <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500 text-white rounded-lg font-medium transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed" disabled={!teamStatus.isComplete}>
                            Lưu Lịch Mổ
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderDetailModal = () => {
        if (!isDetailModalOpen || !selectedSurgery) return null;

        const availableStaff = staffList.filter(s => !selectedSurgery.team.includes(s.id));
        const currentTeamDetails = selectedSurgery.team.map(id => staffList.find(s => s.id === id));

        return (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-[700px] max-h-[90vh] flex flex-col transition-colors">
                    <div className={`flex justify-between items-center p-4 lg:p-5 border-b rounded-t-xl transition-colors ${selectedSurgery.warning
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-900/50'
                        : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
                        }`}>
                        <div>
                            <h2 className="text-lg lg:text-xl font-bold text-gray-800 dark:text-gray-100">Chi tiết lịch mổ</h2>
                            {selectedSurgery.warning && (
                                <p className="text-sm text-red-600 dark:text-red-400 font-semibold mt-1 flex items-center gap-1">
                                    <AlertCircle className="w-4 h-4" /> CẢNH BÁO: {selectedSurgery.warningMsg}
                                </p>
                            )}
                        </div>
                        <button onClick={() => setIsDetailModalOpen(false)} className="text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors">
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    <div className="p-4 lg:p-6 overflow-y-auto flex-1 flex flex-col sm:flex-row gap-6">
                        {/* Left Col: Info */}
                        <div className="w-full sm:w-1/2 space-y-4">
                            <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase">Dịch vụ</p>
                                <p className="text-lg font-bold text-gray-900 dark:text-white">{selectedSurgery.title}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase">Khách hàng</p>
                                <p className="text-md text-gray-800 dark:text-gray-200 font-medium">{selectedSurgery.patient}</p>
                            </div>
                            <div className="flex gap-4">
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase">Thời gian</p>
                                    <p className="text-md text-gray-800 dark:text-gray-200 flex items-center gap-1"><Clock className="w-4 h-4" /> {selectedSurgery.time}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase">Trạng thái</p>
                                    <span className="inline-block mt-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-semibold rounded-md">
                                        {selectedSurgery.status}
                                    </span>
                                </div>
                            </div>

                            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                                <p className="text-sm font-bold text-gray-800 dark:text-gray-100 mb-3">Ê-kíp hiện tại ({currentTeamDetails.length})</p>
                                <div className="space-y-2">
                                    {currentTeamDetails.map(staff => (
                                        <div key={staff.id} className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-100 dark:border-gray-600 transition-colors">
                                            <img src={staff.avatar} alt={staff.name} className="w-8 h-8 rounded-full border border-gray-200 dark:border-gray-600" />
                                            <div>
                                                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-tight">{staff.name}</p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">{staff.role}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Right Col: Add Staff */}
                        <div className="w-full sm:w-1/2 sm:border-l border-t sm:border-t-0 border-gray-200 dark:border-gray-700 pt-4 sm:pt-0 sm:pl-6 flex flex-col">
                            <p className="text-sm font-bold text-gray-800 dark:text-gray-100 mb-1">Điều động nhân sự bổ sung</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Click để thêm người vào ca này</p>

                            <div className="flex-1 overflow-y-auto space-y-2 pr-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
                                {availableStaff.map(staff => (
                                    <div key={staff.id} className="flex items-center justify-between p-3 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all group cursor-pointer" onClick={() => handleAddStaffToSurgery(staff.id)}>
                                        <div className="flex items-center gap-3">
                                            <img src={staff.avatar} alt={staff.name} className="w-8 h-8 rounded-full grayscale group-hover:grayscale-0 transition-all border border-gray-200 dark:border-gray-600" />
                                            <div>
                                                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 group-hover:text-blue-700 dark:group-hover:text-blue-400 leading-tight transition-colors">{staff.name}</p>
                                                <p className="text-xs text-gray-500 dark:text-gray-500 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors">{staff.role}</p>
                                            </div>
                                        </div>
                                        <Plus className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" />
                                    </div>
                                ))}
                                {availableStaff.length === 0 && (
                                    <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">Tất cả nhân viên đã tham gia.</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };


    return (
        <Layout>
            <div>
                {/* Main Content Area */}
                <div className="flex-1 flex flex-col transition-colors">

                    {/* Header Actions */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between border-b border-gray-200 dark:border-gray-700 gap-4 shrink-0 relative z-19 transition-colors pb-4">

                        {/* Left: Navigation & Mobile Menu */}
                        <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-start">
                            <div className="flex items-center gap-3">
                                <button
                                    className="lg:hidden p-2 -ml-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
                                    onClick={() => setIsSidebarOpen(true)}
                                >
                                    <Menu className="w-5 h-5" />
                                </button>
                                <button className="hidden sm:block px-4 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                    Hôm nay
                                </button>
                                <div className="flex items-center bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md transition-colors">
                                    <button className="p-1.5 hover:bg-gray-50 dark:hover:bg-gray-700 border-r border-gray-300 dark:border-gray-700 transition-colors text-gray-600 dark:text-gray-300">
                                        <ChevronLeft className="w-4 h-4" />
                                    </button>

                                    {/* Date Picker Trigger Wrapper */}
                                    <div className="relative">
                                        <button
                                            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                            onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
                                        >
                                            <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                            <span className="hidden sm:inline">Tháng {currentMonth}/{currentYear}</span>
                                            <span className="sm:hidden">{currentMonth}/{currentYear}</span>
                                        </button>

                                        {/* Date Picker Popover */}
                                        {isDatePickerOpen && (
                                            <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xl rounded-xl w-64 p-4 z-50 transition-colors">
                                                {/* Year Navigation */}
                                                <div className="flex justify-between items-center mb-4">
                                                    <button
                                                        onClick={() => setCurrentYear(y => y - 1)}
                                                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-gray-600 dark:text-gray-300"
                                                    >
                                                        <ChevronLeft className="w-5 h-5 font-bold" />
                                                    </button>
                                                    <span className="font-bold text-gray-800 dark:text-gray-100 text-lg">{currentYear}</span>
                                                    <button
                                                        onClick={() => setCurrentYear(y => y + 1)}
                                                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-gray-600 dark:text-gray-300"
                                                    >
                                                        <ChevronRight className="w-5 h-5 font-bold" />
                                                    </button>
                                                </div>

                                                {/* Month Grid */}
                                                <div className="grid grid-cols-3 gap-2">
                                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => (
                                                        <button
                                                            key={month}
                                                            onClick={() => {
                                                                setCurrentMonth(month);
                                                                setIsDatePickerOpen(false);
                                                            }}
                                                            className={`
                                                                py-3 text-center text-sm font-medium rounded-lg transition-colors
                                                                ${currentMonth === month
                                                                    ? 'bg-orange-600 text-white'
                                                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                                                }
                                                            `}
                                                        >
                                                            {month}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <button className="p-1.5 hover:bg-gray-50 dark:hover:bg-gray-700 border-l border-gray-300 dark:border-gray-700 transition-colors text-gray-600 dark:text-gray-300">
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Right: Actions */}
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
                            <button
                                className="flex-1 sm:flex-none justify-center flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900/50 rounded-md bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                                onClick={() => {
                                    setCreateData({ roomId: '', dayIndex: 0, patient: '', service: '', time: '', selectedStaff: [] });
                                    setIsCreateModalOpen(true);
                                }}
                            >
                                <Plus className="w-4 h-4" />
                                <span className="hidden sm:inline">Tạo lịch mổ</span>
                                <span className="sm:hidden">Tạo mới</span>
                            </button>
                            <button className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                                <Filter className="w-4 h-4" />
                                Tất cả
                            </button>

                            <button className="hidden sm:flex items-center gap-1.5 px-2 py-1.5 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors">
                                <Settings className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Calendar Grid Container */}
                    <div className="flex-1 overflow-auto pt-4 transition-colors">
                        {/* Horizontal Scroll Wrapper for small screens */}
                        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm bg-white dark:bg-gray-800 transition-colors">
                            <div className="min-w-[800px] lg:min-w-[1000px]">

                                {/* Grid Header (Days) */}
                                <div className="grid grid-cols-[120px_repeat(7,1fr)] sm:grid-cols-[150px_repeat(7,1fr)] lg:grid-cols-[200px_repeat(7,1fr)] border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 sticky top-0 z-10 transition-colors">
                                    <div className="p-2 sm:p-4 border-r border-gray-200 dark:border-gray-700"></div> {/* Empty top-left cell */}
                                    {daysOfWeek.map((day, index) => (
                                        <div key={index} className={`p-2 sm:p-4 text-center border-r border-gray-200 dark:border-gray-700 last:border-r-0 transition-colors ${day.isToday ? 'bg-orange-50 dark:bg-orange-900/10' : ''}`}>
                                            <div className={`text-[10px] sm:text-xs font-semibold mb-0.5 sm:mb-1 ${day.isToday ? 'text-orange-600 dark:text-orange-500' : 'text-gray-500 dark:text-gray-400'}`}>{day.name}</div>
                                            <div className={`text-lg sm:text-xl font-bold ${day.isToday ? 'text-orange-600 dark:text-orange-500' : 'text-gray-800 dark:text-gray-200'}`}>{day.date}</div>
                                        </div>
                                    ))}
                                </div>

                                {/* Grid Body (Rooms x Days) */}
                                <div>
                                    {rooms.map((room) => (
                                        <div key={room.id} className="grid grid-cols-[120px_repeat(7,1fr)] sm:grid-cols-[150px_repeat(7,1fr)] lg:grid-cols-[200px_repeat(7,1fr)] border-b border-gray-200 dark:border-gray-700 last:border-b-0 min-h-[120px] sm:min-h-[140px] transition-colors">

                                            {/* Room Name Column */}
                                            <div className="p-2 sm:p-4 flex flex-col items-center justify-center border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-center transition-colors">
                                                <span className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300">{room.name}</span>
                                                <Settings className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 dark:text-gray-500 mt-1 sm:mt-2" />
                                            </div>

                                            {/* Day Cells for Room */}
                                            {daysOfWeek.map((_, dayIndex) => {
                                                // Find surgery for this cell
                                                const cellSurgery = surgeries.find(s => s.roomId === room.id && s.dayIndex === dayIndex);

                                                return (
                                                    <div
                                                        key={`${room.id}-${dayIndex}`}
                                                        className="p-1.5 sm:p-2 border-r border-gray-200 dark:border-gray-700 last:border-r-0 relative group hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer flex flex-col items-center justify-center transition-colors"
                                                        onClick={() => handleCellClick(room.id, dayIndex)}
                                                    >
                                                        {/* Hover Add Button */}
                                                        {!cellSurgery && (
                                                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all absolute">
                                                                <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                                                            </div>
                                                        )}

                                                        {/* Surgery Card */}
                                                        {cellSurgery && (
                                                            <div
                                                                className={`w-full h-full rounded-md p-1.5 sm:p-2 flex flex-col text-left border transition-all relative overflow-hidden ${cellSurgery.warning
                                                                    ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-800 shadow-sm hover:border-red-400 dark:hover:border-red-600'
                                                                    : 'bg-white dark:bg-gray-800 border-transparent dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-sm'
                                                                    }`}
                                                                onClick={(e) => handleSurgeryClick(cellSurgery, e)}
                                                            >
                                                                {/* Orange dot indicator */}
                                                                <div className="flex items-center gap-1 sm:gap-1.5 mb-1">
                                                                    <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full shrink-0 ${cellSurgery.warning ? 'bg-red-500 animate-pulse' : 'bg-orange-500'}`}></div>
                                                                    <span className={`text-[10px] sm:text-xs font-bold truncate ${cellSurgery.warning ? 'text-red-700 dark:text-red-400' : 'text-gray-800 dark:text-gray-200'}`}>
                                                                        {cellSurgery.title}
                                                                    </span>
                                                                </div>

                                                                <div className="text-[9px] sm:text-[11px] text-gray-500 dark:text-gray-400 mb-1 truncate">{cellSurgery.time}</div>

                                                                {/* Warning Message */}
                                                                {cellSurgery.warning ? (
                                                                    <div className="mt-auto">
                                                                        <span className="inline-flex px-1 sm:px-1.5 py-0.5 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-[9px] sm:text-[10px] font-bold rounded items-center gap-1 w-full truncate">
                                                                            <AlertCircle className="w-2.5 h-2.5 shrink-0" /> <span className="truncate">Thiếu nhân sự</span>
                                                                        </span>
                                                                    </div>
                                                                ) : (
                                                                    <div className="mt-auto">
                                                                        <span className="inline-block px-1 sm:px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[9px] sm:text-[10px] font-semibold rounded truncate max-w-full">
                                                                            {cellSurgery.status}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals */}
            {renderCreateModal()}
            {renderDetailModal()}

        </Layout>
    );
}